import { Fragment, useState } from "react";
import { XMarkIcon } from "@heroicons/react/24/solid";
import PageWrapper from "../../common/components/PageWrapper";
import IncentiveMetadata from "../components/IncentiveMetadata";
import { MonthlyPeriod, UserType } from "global";
import ListPageTopSection from "../../common/components/ListPageTopSection";
import Input from "../../common/components/Input";
import IncentiveLineGraph from "../components/IncentiveBarGraph";
import BoxWithShadow from "../../common/components/BoxWithShadow";
import { useForm } from "../../../hooks/useForm";
import { ChevronDownIcon } from "@heroicons/react/24/outline";
import IncentiveTable from "../components/IncentiveTable";
import { useModal } from "../../../hooks/useModal";
import SearchUserModal from "../components/SearchUserModal";

const FUTURE_ERROR = "현재 년/월 이상은 선택할 수 없습니다";

type IncentiveFilters = {
  query: string;
  searchType: UserType;
  userId: string;
};

export type SelectUserParams = {
  id: string;
  name: string;
  type: UserType;
};

export interface SharedIncentiveComponentProps {
  period: MonthlyPeriod;
  filters: IncentiveFilters;
}

export default function IncentiveDashboardContainer() {
  const [isSearchModalOpen, { open, close }] = useModal();

  const today = new Date();
  const todayYear = today.getFullYear();
  const todayMonth = today.getMonth() + 1;

  /**
   * Note: Html type=month input accepts and returns string values in 'yyyy-mm' format
   */
  const maxPeriod = `${todayYear}-${todayMonth.toString().padStart(2, "0")}`;

  const [period, setPeriod] = useState<MonthlyPeriod>({
    year: todayYear,
    month: todayMonth,
  });

  const [filters, , onBatch] = useForm<IncentiveFilters>({
    query: "",
    searchType: "caregivers",
    userId: "",
  });

  /**
   * Handler for when user opens up the date picker dropdown and selects a specific year/month value
   */
  const onPeriodChange = (selected: string) => {
    if (!selected) {
      return setPeriod({
        year: todayYear,
        month: todayMonth,
      });
    }

    const [newYear, newMonth] = selected.split("-").map((item) => +item);

    if (
      newYear === 1 ||
      newYear > todayYear ||
      (newYear === todayYear && newMonth > todayMonth)
    ) {
      alert(FUTURE_ERROR);
      return;
    }

    setPeriod({
      year: newYear,
      month: newMonth,
    });
  };

  /**
   * Handler for when user changes the date by pressing up/down on year or month
   */
  const onKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (period.month === 1 && event.key === "ArrowDown") {
      event.preventDefault();

      setPeriod({
        year: period.year - 1,
        month: 12,
      });
    }

    if (period.month === 12 && event.key === "ArrowUp") {
      event.preventDefault();
      const newYear = period.year + 1;

      const isFuture =
        newYear > todayYear || (newYear === todayYear && 1 > todayMonth);

      if (isFuture) {
        alert(FUTURE_ERROR);
        return;
      }

      setPeriod({
        year: newYear,
        month: 1,
      });
    }
  };

  const isCaregiver = filters.searchType === "caregivers";

  const onUserSelect = ({ id, name, type }: SelectUserParams) => {
    onBatch({
      userId: id,
      query: name,
      searchType: type,
    });
    close();
  };

  const onSearchCancel = () => {
    onBatch({
      query: "",
      searchType: "caregivers",
      userId: "",
    });
  };

  return (
    <Fragment>
      {isSearchModalOpen && (
        <SearchUserModal
          open={isSearchModalOpen}
          onClose={close}
          onSelect={onUserSelect}
        />
      )}
      <PageWrapper>
        <ListPageTopSection>
          <Input
            type="month"
            className="w-1/6"
            value={`${period.year}-${period.month.toString().padStart(2, "0")}`}
            defaultValue={maxPeriod}
            max={maxPeriod}
            onKeyDown={onKeyDown}
            onValueChange={onPeriodChange}
          />
        </ListPageTopSection>
        <div className="flex h-full gap-1 overflow-hidden">
          <IncentiveMetadata period={period} />
          <BoxWithShadow className="flex h-full w-full flex-col gap-3 overflow-hidden">
            <div className="flex h-[45px] gap-1">
              <div className="flex w-fit cursor-pointer gap-1" onClick={open}>
                <div className="flex h-full w-[120px] items-center justify-between rounded-md border border-zinc-300 px-3">
                  <span className="text-zinc-700">
                    {isCaregiver ? "보호사" : "보호자"}
                  </span>
                  <ChevronDownIcon className="w-4 text-black" />
                </div>
                <div className="flex h-full w-[300px] items-center justify-start rounded-md border border-zinc-300 px-3">
                  <span
                    className={
                      filters.query ? "text-zinc-700" : "text-zinc-300"
                    }
                  >
                    {filters.query
                      ? filters.query
                      : `${isCaregiver ? "보호사" : "보호자"} 이름`}
                  </span>
                </div>
              </div>
              {filters.query && filters.userId && (
                <button
                  className="ml-3 flex items-center text-sm text-zinc-500 transition-colors duration-300 hover:text-zinc-700"
                  onClick={onSearchCancel}
                >
                  <span>검색 취소</span>
                  <XMarkIcon className="h-5 w-5" />
                </button>
              )}
            </div>
            <div className="h-full overflow-y-auto">
              <IncentiveLineGraph period={period} filters={filters} />
              <IncentiveTable period={period} filters={filters} />
            </div>
          </BoxWithShadow>
        </div>
      </PageWrapper>
    </Fragment>
  );
}
